'use client';

import { useEffect } from 'react';

const BLOG_HEADER_HEIGHT_PROPERTY = '--blog-header-height';

const syncBlogHeaderHeight = () => {
  if (typeof document === 'undefined') return;

  const header = document.getElementById('header');
  const headerHeight = header?.getBoundingClientRect().height || 0;

  if (headerHeight > 0) {
    document.documentElement.style.setProperty(BLOG_HEADER_HEIGHT_PROPERTY, `${headerHeight}px`);
  } else {
    document.documentElement.style.removeProperty(BLOG_HEADER_HEIGHT_PROPERTY);
  }
};

export const useBlogHeaderHeight = () => {
  useEffect(() => {
    if (typeof document === 'undefined') return;

    let observedHeader: HTMLElement | null = null;
    let resizeObserver: ResizeObserver | null = null;

    const disconnectResizeObserver = () => {
      resizeObserver?.disconnect();
      resizeObserver = null;
    };

    const observeHeader = () => {
      const header = document.getElementById('header');
      if (header === observedHeader) {
        syncBlogHeaderHeight();
        return;
      }

      disconnectResizeObserver();
      observedHeader = header;

      if (header && typeof ResizeObserver !== 'undefined') {
        resizeObserver = new ResizeObserver(syncBlogHeaderHeight);
        resizeObserver.observe(header);
      }

      syncBlogHeaderHeight();
    };

    observeHeader();

    const mutationObserver = new MutationObserver(observeHeader);
    mutationObserver.observe(document.body, {
      childList: true,
      subtree: true,
    });

    window.addEventListener('resize', syncBlogHeaderHeight);

    return () => {
      disconnectResizeObserver();
      mutationObserver.disconnect();
      window.removeEventListener('resize', syncBlogHeaderHeight);
      document.documentElement.style.removeProperty(BLOG_HEADER_HEIGHT_PROPERTY);
    };
  }, []);
};
